import React, { useCallback, useEffect, useRef, useState } from 'react';
import { examService } from '../services/api';
import { useApp } from '../context/AppContext';

const EMPTY_QUESTION = {
  type: 'mcq', text: '', options: ['', '', '', ''], correctIndex: '', accepted: '', sentence: '', maxPoints: '5',
};

const TYPE_LABELS = {
  mcq: 'Multiple choice',
  true_false: 'Richtig / Falsch',
  fill_blank: 'Fill in the blank',
  ordering: 'Sentence ordering',
  listening: 'Listening (Hören)',
  writing: 'Writing (Schreiben)',
};

const statusBadge = (status) => {
  if (status === 'published') return <span className="badge badge-active">Published</span>;
  if (status === 'closed') return <span className="badge badge-ended">Closed</span>;
  return <span className="badge">Draft</span>;
};

const QuestionForm = ({ examId, editing, onSaved, onCancelEdit, notify }) => {
  const [form, setForm] = useState(EMPTY_QUESTION);
  const [saving, setSaving] = useState(false);
  const audioFileRef = useRef(null);

  useEffect(() => {
    if (editing) {
      const type = editing.type || 'mcq';
      const options = (type === 'mcq' || type === 'listening') ? [...editing.options] : ['', '', '', ''];
      while (options.length < 4) options.push('');
      setForm({
        type,
        text: editing.text,
        options,
        correctIndex: editing.correctIndex != null ? String(editing.correctIndex) : '',
        accepted: type === 'fill_blank' ? (editing.answer.accepted || []).join(', ') : '',
        sentence: type === 'ordering' ? (editing.answer.words || []).join(' ') : '',
        maxPoints: type === 'writing' ? String(editing.answer.maxPoints) : '5',
      });
    } else {
      setForm(EMPTY_QUESTION);
    }
    if (audioFileRef.current) audioFileRef.current.value = '';
  }, [editing]);

  const setOption = (i, value) => {
    setForm((f) => {
      const options = [...f.options];
      options[i] = value;
      return { ...f, options };
    });
  };

  // Shared by mcq and listening — both are "pick the correct option".
  // Blank option slots are dropped; the correct answer is re-indexed
  // against the kept ones so it survives a gap (e.g. option 2 left
  // empty with the answer on option 3).
  const buildChoicePayload = (type) => {
    const kept = form.options
      .map((text, index) => ({ text: text.trim(), index }))
      .filter((o) => o.text);
    if (kept.length < 2) {
      notify('Fill in at least 2 answer options', 'error');
      return null;
    }
    const correctIndex = kept.findIndex((o) => o.index === Number(form.correctIndex));
    if (form.correctIndex === '' || correctIndex === -1) {
      notify('Pick which option is the correct answer', 'error');
      return null;
    }
    return { type, text: form.text, options: kept.map((o) => o.text), correctIndex };
  };

  // Per-type payload for the API; returns null (after notifying) when
  // the form isn't valid for its type. Listening is handled separately
  // in handleSubmit (it needs multipart for the audio file).
  const buildPayload = () => {
    if (form.type === 'mcq') return buildChoicePayload('mcq');
    if (form.type === 'true_false') {
      if (form.correctIndex !== '0' && form.correctIndex !== '1') {
        notify('Pick whether the statement is Richtig or Falsch', 'error');
        return null;
      }
      return { type: 'true_false', text: form.text, correctIndex: Number(form.correctIndex) };
    }
    if (form.type === 'fill_blank') {
      if (!form.text.includes('___')) {
        notify('Put ___ in the question text where the gap is', 'error');
        return null;
      }
      const accepted = form.accepted.split(',').map((s) => s.trim()).filter(Boolean);
      if (accepted.length === 0) {
        notify('Give at least one accepted answer', 'error');
        return null;
      }
      return { type: 'fill_blank', text: form.text, accepted };
    }
    if (form.type === 'ordering') {
      const words = form.sentence.trim().split(/\s+/).filter(Boolean);
      if (words.length < 2) {
        notify('Enter the correct sentence (at least 2 words)', 'error');
        return null;
      }
      return {
        type: 'ordering',
        text: form.text.trim() || 'Ordnen Sie die Wörter zu einem richtigen Satz.',
        words,
      };
    }
    // writing
    const maxPoints = Number(form.maxPoints);
    if (!Number.isInteger(maxPoints) || maxPoints < 1 || maxPoints > 100) {
      notify('Max points must be a whole number from 1 to 100', 'error');
      return null;
    }
    return { type: 'writing', text: form.text, maxPoints };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.type === 'listening') {
      const choice = buildChoicePayload('listening');
      if (!choice) return;
      const file = audioFileRef.current && audioFileRef.current.files[0];
      if (!editing && !file) return notify('Choose an audio file', 'error');

      setSaving(true);
      try {
        if (editing) {
          await examService.updateListeningQuestion(examId, editing.id, choice, file);
          notify('Question updated', 'success');
        } else {
          await examService.addListeningQuestion(examId, choice, file);
          notify('Question added', 'success');
        }
        setForm(EMPTY_QUESTION);
        if (audioFileRef.current) audioFileRef.current.value = '';
        onSaved();
      } catch (err) {
        notify(err.message, 'error');
      } finally {
        setSaving(false);
      }
      return;
    }

    const payload = buildPayload();
    if (!payload) return;

    setSaving(true);
    try {
      if (editing) {
        await examService.updateQuestion(examId, editing.id, payload);
        notify('Question updated', 'success');
      } else {
        await examService.addQuestion(examId, payload);
        notify('Question added', 'success');
      }
      setForm(EMPTY_QUESTION);
      onSaved();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="exam-question-form" onSubmit={handleSubmit}>
      <h3>{editing ? 'Edit question' : 'Add a question'}</h3>
      <select
        value={form.type}
        onChange={(e) => setForm((f) => ({ ...EMPTY_QUESTION, type: e.target.value, text: f.text }))}
        disabled={!!editing}
        title={editing ? 'A question keeps its type — delete and re-add to change it' : 'Question type'}
      >
        {Object.entries(TYPE_LABELS).map(([value, label]) => (
          <option key={value} value={value}>{label}</option>
        ))}
      </select>
      <textarea
        placeholder={
          form.type === 'fill_blank'
            ? 'Question text with ___ for the gap (e.g. Ich ___ aus Indien.)'
            : form.type === 'ordering'
              ? 'Instruction (optional — defaults to „Ordnen Sie die Wörter…“)'
              : form.type === 'true_false'
                ? 'Statement (e.g. Berlin ist die Hauptstadt von Deutschland.)'
                : form.type === 'listening'
                  ? 'Question about the audio (e.g. Was hört man?)'
                  : form.type === 'writing'
                    ? 'Writing prompt (e.g. Schreiben Sie 3–4 Sätze über sich selbst.)'
                    : 'Question text'
        }
        value={form.text}
        onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
        rows={form.type === 'writing' ? 3 : 2}
        required={form.type !== 'ordering'}
      />

      {form.type === 'mcq' && (
        <>
          {form.options.map((option, i) => (
            <label key={i} className="exam-option-input">
              <input
                type="radio"
                name="correct-option"
                checked={form.correctIndex === String(i)}
                onChange={() => setForm((f) => ({ ...f, correctIndex: String(i) }))}
                disabled={!option.trim()}
                title="Mark as the correct answer"
              />
              <input
                placeholder={`Option ${i + 1}${i >= 2 ? ' (optional)' : ''}`}
                value={option}
                onChange={(e) => setOption(i, e.target.value)}
              />
            </label>
          ))}
          <p className="stat-note">Select the radio button next to the correct answer.</p>
        </>
      )}

      {form.type === 'true_false' && (
        <div className="exam-tf-choice">
          {['Richtig', 'Falsch'].map((label, i) => (
            <label key={label} className="exam-option-input">
              <input
                type="radio"
                name="correct-option"
                checked={form.correctIndex === String(i)}
                onChange={() => setForm((f) => ({ ...f, correctIndex: String(i) }))}
              />
              <span>{label}</span>
            </label>
          ))}
          <p className="stat-note">Pick whether the statement is true (Richtig) or false (Falsch).</p>
        </div>
      )}

      {form.type === 'fill_blank' && (
        <>
          <input
            placeholder="Accepted answers, comma-separated (e.g. komme)"
            value={form.accepted}
            onChange={(e) => setForm((f) => ({ ...f, accepted: e.target.value }))}
          />
          <p className="stat-note">The student types the missing word — any accepted answer counts, case doesn't matter.</p>
        </>
      )}

      {form.type === 'ordering' && (
        <>
          <input
            placeholder="Correct sentence, words separated by spaces (e.g. Wo wohnst du ?)"
            value={form.sentence}
            onChange={(e) => setForm((f) => ({ ...f, sentence: e.target.value }))}
          />
          <p className="stat-note">The student sees these words shuffled and must tap them into this exact order.</p>
        </>
      )}

      {form.type === 'listening' && (
        <>
          {editing && editing.type === 'listening' && (
            <audio controls src={examService.audioUrl(examId, editing.id)} className="exam-audio-player" />
          )}
          <input type="file" accept="audio/*" ref={audioFileRef} />
          <p className="stat-note">
            {editing ? 'Leave empty to keep the current clip, or choose a new file to replace it.' : 'Upload the audio clip students will listen to.'}
          </p>
          {form.options.map((option, i) => (
            <label key={i} className="exam-option-input">
              <input
                type="radio"
                name="correct-option"
                checked={form.correctIndex === String(i)}
                onChange={() => setForm((f) => ({ ...f, correctIndex: String(i) }))}
                disabled={!option.trim()}
                title="Mark as the correct answer"
              />
              <input
                placeholder={`Option ${i + 1}${i >= 2 ? ' (optional)' : ''}`}
                value={option}
                onChange={(e) => setOption(i, e.target.value)}
              />
            </label>
          ))}
          <p className="stat-note">Select the radio button next to the correct answer.</p>
        </>
      )}

      {form.type === 'writing' && (
        <>
          <label className="exam-maxpoints-input">
            Max points:
            <input
              type="number"
              min="1"
              max="100"
              value={form.maxPoints}
              onChange={(e) => setForm((f) => ({ ...f, maxPoints: e.target.value }))}
            />
          </label>
          <p className="stat-note">
            The student types a free-text answer — a teacher reads it and awards points (0 to the max) after submission.
          </p>
        </>
      )}

      <div className="btn-group">
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Add question'}
        </button>
        {editing && (
          <button type="button" className="btn-secondary" onClick={onCancelEdit}>Cancel</button>
        )}
      </div>
    </form>
  );
};

// Read-only rendering of an exam's questions with the correct answer
// ticked — shared by the draft editor (which adds Edit/Delete per row)
// and the view-questions toggle on a published/closed exam. `examId` is
// only needed to build a listening question's audio preview URL.
const QuestionList = ({ questions, examId, renderActions }) => (
  <ol className="exam-question-list">
    {questions.map((q) => (
      <li key={q.id}>
        <div className="exam-question-row">
          <div>
            <p className="exam-question-text">
              {q.text}
              {q.type && q.type !== 'mcq' && <span className="badge exam-type-badge">{TYPE_LABELS[q.type]}</span>}
            </p>
            {q.type === 'listening' && (
              <audio controls src={examService.audioUrl(examId, q.id)} className="exam-audio-player" />
            )}
            {(!q.type || q.type === 'mcq' || q.type === 'true_false' || q.type === 'listening') && (
              <ul className="exam-question-options">
                {q.options.map((option, i) => (
                  <li key={i} className={i === q.correctIndex ? 'is-correct' : ''}>
                    {option}{i === q.correctIndex ? ' ✓' : ''}
                  </li>
                ))}
              </ul>
            )}
            {q.type === 'fill_blank' && (
              <p className="exam-question-options exam-answer-note">
                Accepted: <span className="is-correct">{q.answer.accepted.join(', ')} ✓</span>
              </p>
            )}
            {q.type === 'ordering' && (
              <p className="exam-question-options exam-answer-note">
                Correct order: <span className="is-correct">{q.answer.words.join(' ')} ✓</span>
              </p>
            )}
            {q.type === 'writing' && (
              <p className="exam-question-options exam-answer-note">
                Free-text answer, graded by a teacher — worth {q.answer.maxPoints} point{q.answer.maxPoints === 1 ? '' : 's'}.
              </p>
            )}
          </div>
          {renderActions && <div className="btn-group">{renderActions(q)}</div>}
        </div>
      </li>
    ))}
  </ol>
);

// Lets a teacher award points for one student's writing answers on one
// attempt. Local score inputs default to whatever's already been
// awarded so re-opening a graded attempt shows its current marks.
const GradingPanel = ({ examId, attempt, notify, onGraded }) => {
  const [scores, setScores] = useState(
    Object.fromEntries(attempt.writingAnswers.map((w) => [w.questionId, w.awarded != null ? String(w.awarded) : '']))
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const payload = {};
    for (const w of attempt.writingAnswers) {
      const raw = scores[w.questionId];
      if (raw === '' || raw === undefined) continue;
      const n = Number(raw);
      if (!Number.isInteger(n) || n < 0 || n > w.maxPoints) {
        return notify(`Score must be a whole number from 0 to ${w.maxPoints}`, 'error');
      }
      payload[w.questionId] = n;
    }
    if (Object.keys(payload).length === 0) return notify('Enter at least one score', 'error');

    setSaving(true);
    try {
      await examService.gradeAttempt(examId, attempt.id, payload);
      notify('Grades saved', 'success');
      onGraded();
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="exam-grading-panel">
      {attempt.writingAnswers.map((w) => (
        <div key={w.questionId} className="exam-grading-item">
          <p className="exam-question-text">{w.prompt}</p>
          <p className="exam-writing-answer">{w.answer || <em>No answer given.</em>}</p>
          <label className="exam-maxpoints-input">
            Score (max {w.maxPoints}):
            <input
              type="number"
              min="0"
              max={w.maxPoints}
              value={scores[w.questionId] ?? ''}
              onChange={(e) => setScores((s) => ({ ...s, [w.questionId]: e.target.value }))}
              className="exam-grade-input"
            />
          </label>
        </div>
      ))}
      <button className="btn-primary" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving…' : 'Save grades'}
      </button>
    </div>
  );
};

const StaffExamDetail = ({ examId, onBack, onChanged, notify }) => {
  const [exam, setExam] = useState(null);
  const [results, setResults] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showQuestions, setShowQuestions] = useState(false);
  const [gradingAttemptId, setGradingAttemptId] = useState(null);

  const loadExam = useCallback(() => {
    examService.get(examId).then(setExam).catch((err) => notify(err.message, 'error'));
  }, [examId, notify]);

  const loadResults = useCallback(() => {
    examService.results(examId).then(setResults).catch((err) => notify(err.message, 'error'));
  }, [examId, notify]);

  useEffect(() => {
    loadExam();
  }, [loadExam]);

  useEffect(() => {
    if (exam && exam.status !== 'draft') loadResults();
  }, [exam, loadResults]);

  if (!exam) return null;

  const handlePublish = async () => {
    const confirmed = window.confirm(
      `Publish "${exam.title}"? Students in an active ${exam.level} batch will be able to take it, and questions can no longer be edited.`
    );
    if (!confirmed) return;
    try {
      await examService.publish(exam.id);
      notify('Exam published', 'success');
      loadExam();
      onChanged();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleClose = async () => {
    const confirmed = window.confirm(`Close "${exam.title}"? Students will no longer be able to start it.`);
    if (!confirmed) return;
    try {
      await examService.close(exam.id);
      notify('Exam closed', 'success');
      loadExam();
      onChanged();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  const handleDeleteQuestion = async (q) => {
    const confirmed = window.confirm('Delete this question?');
    if (!confirmed) return;
    try {
      await examService.removeQuestion(exam.id, q.id);
      if (editingQuestion && editingQuestion.id === q.id) setEditingQuestion(null);
      loadExam();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2>{exam.title} <span className="badge">{exam.level}</span> {statusBadge(exam.status)}</h2>
        <button className="btn-link" onClick={onBack}>← All exams</button>
      </div>
      <p className="stat-note">
        {exam.durationMinutes} minute{exam.durationMinutes === 1 ? '' : 's'} · one attempt per student ·{' '}
        {exam.questions.some((q) => q.type === 'writing')
          ? 'auto-graded, except writing questions which you grade below'
          : 'auto-graded'}
      </p>

      {exam.status === 'draft' ? (
        <>
          <QuestionList
            questions={exam.questions}
            examId={exam.id}
            renderActions={(q) => (
              <>
                <button className="btn-secondary" onClick={() => setEditingQuestion(q)}>Edit</button>
                <button className="btn-secondary btn-danger" onClick={() => handleDeleteQuestion(q)}>Delete</button>
              </>
            )}
          />
          {exam.questions.length === 0 && <p>No questions yet — add the first one below.</p>}

          <QuestionForm
            examId={exam.id}
            editing={editingQuestion}
            onSaved={() => { setEditingQuestion(null); loadExam(); }}
            onCancelEdit={() => setEditingQuestion(null)}
            notify={notify}
          />

          {exam.questions.length > 0 && (
            <div className="exam-publish-row">
              <button className="btn-primary" onClick={handlePublish}>
                Publish exam ({exam.questions.length} question{exam.questions.length === 1 ? '' : 's'})
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="exam-publish-row btn-group">
            <button className="btn-secondary" onClick={() => setShowQuestions((s) => !s)}>
              {showQuestions ? 'Hide questions' : 'View questions'}
            </button>
            {exam.status === 'published' && (
              <button className="btn-secondary" onClick={handleClose}>Close exam</button>
            )}
          </div>
          {showQuestions && (
            <>
              <h3>Questions</h3>
              <QuestionList questions={exam.questions} examId={exam.id} />
            </>
          )}
          <h3>Results</h3>
          {results && results.results.length === 0 && <p>No students have taken this exam yet.</p>}
          {results && results.results.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Submitted</th>
                    {results.results.some((r) => r.writingAnswers.length > 0) && <th>Writing</th>}
                  </tr>
                </thead>
                <tbody>
                  {results.results.map((r) => (
                    <React.Fragment key={r.id}>
                      <tr>
                        <td>
                          {r.student ? (
                            <>{r.student.name} <span className="student-id-tag">#{r.student.id}</span></>
                          ) : (
                            `Student #${r.id}`
                          )}
                        </td>
                        <td>{r.status === 'pending_review' ? '—' : r.submittedAt ? `${r.score} / ${r.totalQuestions}` : '—'}</td>
                        <td>
                          {r.status === 'submitted' && <span className="badge badge-active">Submitted</span>}
                          {r.status === 'pending_review' && <span className="badge badge-recording">Pending review</span>}
                          {r.status === 'in_progress' && <span className="badge badge-recording">In progress</span>}
                          {r.status === 'expired' && <span className="badge badge-danger">Time up</span>}
                        </td>
                        <td>{r.submittedAt ? new Date(r.submittedAt).toLocaleString() : '—'}</td>
                        {results.results.some((x) => x.writingAnswers.length > 0) && (
                          <td>
                            {r.writingAnswers.length > 0 && (
                              <button
                                className="btn-secondary"
                                onClick={() => setGradingAttemptId((id) => (id === r.id ? null : r.id))}
                              >
                                {gradingAttemptId === r.id ? 'Hide' : 'Grade'}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                      {gradingAttemptId === r.id && (
                        <tr>
                          <td colSpan={5}>
                            <GradingPanel
                              examId={exam.id}
                              attempt={r}
                              notify={notify}
                              onGraded={() => { setGradingAttemptId(null); loadResults(); }}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// No create-exam form here on purpose: exams are authored by AI and
// arrive as drafts (the POST /exams API still exists for that path) —
// staff review/edit the draft's questions and publish it. Teachers and
// admins share the same abilities, bounded only by the levels a teacher
// actually teaches.
const StaffExams = () => {
  const { notify, currentUser } = useApp();
  const isAdmin = currentUser.role === 'admin';
  const [exams, setExams] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState(null);

  const loadExams = useCallback(() => {
    examService.list().then(setExams).catch((err) => notify(err.message, 'error'));
  }, [notify]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  const handleDelete = async (exam) => {
    const confirmed = window.confirm(
      `Delete "${exam.title}"? Its questions and every student attempt/score will be deleted too.`
    );
    if (!confirmed) return;
    try {
      await examService.remove(exam.id);
      notify('Exam deleted', 'success');
      loadExams();
    } catch (err) {
      notify(err.message, 'error');
    }
  };

  if (selectedExamId) {
    return (
      <div className="page">
        <h1>Exams</h1>
        <StaffExamDetail
          examId={selectedExamId}
          onBack={() => setSelectedExamId(null)}
          onChanged={loadExams}
          notify={notify}
        />
      </div>
    );
  }

  return (
    <div className="page">
      <h1>Exams</h1>
      <div className="card">
        <p className="stat-note">
          Exams are prepared by AI and appear here as drafts — review the questions, edit them or the correct
          answers, then publish. A published exam is visible only to students enrolled in an active batch at its
          level: multiple-choice, auto-graded, one timed attempt per student.
        </p>

        <ul className="list">
          {exams.map((exam) => (
            <li key={exam.id}>
              <span>
                <strong>{exam.title}</strong> <span className="badge">{exam.level}</span> {statusBadge(exam.status)}
                {' '}· {exam.questionCount} question{exam.questionCount === 1 ? '' : 's'} · {exam.durationMinutes} min
                {exam.status !== 'draft' && ` · ${exam.submittedCount} submitted`}
              </span>
              <div className="btn-group actions-group">
                <button className="btn-secondary" onClick={() => setSelectedExamId(exam.id)}>
                  {exam.status === 'draft' ? 'Review & edit' : 'View'}
                </button>
                {(isAdmin || exam.createdBy === currentUser.id) && (
                  <button className="btn-secondary btn-danger" onClick={() => handleDelete(exam)}>Delete</button>
                )}
              </div>
            </li>
          ))}
          {exams.length === 0 && <li>No exams yet — they'll appear here once prepared.</li>}
        </ul>
      </div>
    </div>
  );
};

const formatClock = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

// Tap-to-build word ordering: `chosen` is an array of indices into the
// (shuffled) word list, so duplicate words stay distinguishable. Tapping
// a bank word appends it; tapping a chosen word puts it back.
const OrderingAnswer = ({ q, chosen, onChange }) => {
  const remaining = q.words.map((w, i) => i).filter((i) => !chosen.includes(i));
  return (
    <div>
      <div className="exam-word-sequence">
        {chosen.length === 0 && <span className="stat-note exam-word-hint">Tap the words below in the right order…</span>}
        {chosen.map((wi) => (
          <button
            type="button"
            key={wi}
            className="exam-word-chip is-chosen"
            onClick={() => onChange(chosen.filter((x) => x !== wi))}
          >
            {q.words[wi]}
          </button>
        ))}
      </div>
      <div className="exam-word-bank">
        {remaining.map((wi) => (
          <button type="button" key={wi} className="exam-word-chip" onClick={() => onChange([...chosen, wi])}>
            {q.words[wi]}
          </button>
        ))}
      </div>
    </div>
  );
};

const isAnswered = (q, value) => {
  if (q.type === 'fill_blank' || q.type === 'writing') return typeof value === 'string' && value.trim() !== '';
  if (q.type === 'ordering') return Array.isArray(value) && value.length === q.words.length;
  return value !== undefined;
};

// The in-exam view: countdown, questions, one submit (auto-fired when
// the clock hits zero). The countdown is anchored to the server's
// remainingSeconds from /start, not the device clock's idea of the
// deadline, so a skewed client clock can't stretch or shrink the exam.
const ExamTaking = ({ session, onFinished, notify }) => {
  const [answers, setAnswers] = useState({});
  const [remaining, setRemaining] = useState(session.remainingSeconds);
  const endsAtRef = useRef(Date.now() + session.remainingSeconds * 1000);
  const submittingRef = useRef(false);

  const handleSubmit = useCallback(async (auto = false) => {
    if (submittingRef.current) return;
    if (!auto) {
      const unanswered = session.questions.filter((q) => !isAnswered(q, answers[q.id])).length;
      const message = unanswered > 0
        ? `Submit the exam? ${unanswered} question${unanswered === 1 ? ' is' : 's are'} unanswered.`
        : 'Submit the exam? You cannot change your answers afterwards.';
      if (!window.confirm(message)) return;
    }
    submittingRef.current = true;
    try {
      // Ordering answers are kept as indices for the UI; the server
      // grades words, so convert on the way out.
      const payload = {};
      for (const q of session.questions) {
        const value = answers[q.id];
        if (q.type === 'ordering') {
          if (Array.isArray(value) && value.length > 0) payload[q.id] = value.map((i) => q.words[i]);
        } else if (q.type === 'fill_blank' || q.type === 'writing') {
          if (typeof value === 'string' && value.trim()) payload[q.id] = value.trim();
        } else if (value !== undefined) {
          payload[q.id] = value;
        }
      }
      const result = await examService.submit(session.examId, payload);
      notify(auto ? 'Time up — your exam was submitted automatically' : 'Exam submitted', 'success');
      onFinished(result);
    } catch (err) {
      submittingRef.current = false;
      notify(err.message, 'error');
      if (/time is up/i.test(err.message)) onFinished(null);
    }
  }, [answers, session, onFinished, notify]);

  useEffect(() => {
    const tick = setInterval(() => {
      const left = Math.max(0, Math.round((endsAtRef.current - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0) {
        clearInterval(tick);
        handleSubmit(true);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [handleSubmit]);

  const answeredCount = session.questions.filter((q) => isAnswered(q, answers[q.id])).length;

  return (
    <div className="page">
      <div className="exam-taking-header">
        <h1>{session.title}</h1>
        <div className={`exam-timer${remaining <= 60 ? ' is-critical' : ''}`}>
          ⏱ {formatClock(remaining)}
        </div>
      </div>
      <p className="stat-note">
        {answeredCount} of {session.questions.length} answered · submits automatically when the timer runs out
      </p>

      {session.questions.map((q, index) => (
        <div key={q.id} className="card exam-taking-question">
          <p className="exam-question-text"><strong>{index + 1}.</strong> {q.text}</p>

          {q.type === 'listening' && (
            <audio controls src={examService.audioUrl(session.examId, q.id)} className="exam-audio-player" />
          )}

          {(q.type === 'mcq' || q.type === 'true_false' || q.type === 'listening') && (
            <div className="exam-taking-options">
              {q.options.map((option, i) => (
                <label key={i} className={`exam-taking-option${answers[q.id] === i ? ' is-selected' : ''}`}>
                  <input
                    type="radio"
                    name={`question-${q.id}`}
                    checked={answers[q.id] === i}
                    onChange={() => setAnswers((a) => ({ ...a, [q.id]: i }))}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          )}

          {q.type === 'fill_blank' && (
            <input
              className="exam-blank-input"
              placeholder="Type the missing word…"
              value={answers[q.id] || ''}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
            />
          )}

          {q.type === 'ordering' && (
            <OrderingAnswer
              q={q}
              chosen={answers[q.id] || []}
              onChange={(chosen) => setAnswers((a) => ({ ...a, [q.id]: chosen }))}
            />
          )}

          {q.type === 'writing' && (
            <>
              <textarea
                className="exam-writing-textarea"
                placeholder="Write your answer…"
                rows={5}
                value={answers[q.id] || ''}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
              />
              <p className="stat-note">Worth {q.maxPoints} point{q.maxPoints === 1 ? '' : 's'} — a teacher reads and grades this after you submit.</p>
            </>
          )}
        </div>
      ))}

      <div className="exam-publish-row">
        <button className="btn-primary" onClick={() => handleSubmit(false)}>Submit exam</button>
      </div>
    </div>
  );
};

const StudentExams = () => {
  const { notify } = useApp();
  const [exams, setExams] = useState([]);
  const [session, setSession] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const loadExams = useCallback(() => {
    examService.mine().then(setExams).catch((err) => notify(err.message, 'error'));
  }, [notify]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  const handleStart = async (exam, resuming) => {
    if (!resuming) {
      const confirmed = window.confirm(
        `Start "${exam.title}"? You get one attempt, and the ${exam.durationMinutes}-minute timer starts immediately.`
      );
      if (!confirmed) return;
    }
    try {
      const data = await examService.start(exam.id);
      setLastResult(null);
      setSession(data);
    } catch (err) {
      notify(err.message, 'error');
      loadExams();
    }
  };

  if (session) {
    return (
      <ExamTaking
        session={session}
        notify={notify}
        onFinished={(result) => {
          setSession(null);
          setLastResult(result);
          loadExams();
        }}
      />
    );
  }

  return (
    <div className="page">
      <h1>Exams</h1>
      {lastResult && (
        <div className="card exam-result-card">
          {lastResult.pendingReview ? (
            <>
              <h2>Exam submitted</h2>
              <p className="stat-note">Your teacher still needs to grade a written answer — your final score will appear here once that's done.</p>
            </>
          ) : (
            <>
              <h2>Your score: {lastResult.score} / {lastResult.totalQuestions}</h2>
              <p className="stat-note">Your answers have been recorded — your teacher can see this result.</p>
            </>
          )}
        </div>
      )}
      {exams.length === 0 ? (
        <div className="card">
          <p>No exams available for your level yet.</p>
        </div>
      ) : (
        exams.map((exam) => (
          <div key={exam.id} className="card">
            <div className="exam-student-row">
              <div>
                <h2>{exam.title} <span className="badge">{exam.level}</span></h2>
                <p className="stat-note">
                  {exam.questionCount} question{exam.questionCount === 1 ? '' : 's'} · {exam.durationMinutes} minutes · one attempt
                </p>
              </div>
              <div>
                {exam.attemptStatus === 'available' && (
                  <button className="btn-primary" onClick={() => handleStart(exam, false)}>Start exam</button>
                )}
                {exam.attemptStatus === 'in_progress' && (
                  <button className="btn-primary" onClick={() => handleStart(exam, true)}>Resume exam</button>
                )}
                {exam.attemptStatus === 'submitted' && (
                  <span className="badge badge-active">Score: {exam.score} / {exam.totalQuestions}</span>
                )}
                {exam.attemptStatus === 'pending_review' && (
                  <span className="badge badge-recording">Submitted — pending review</span>
                )}
                {exam.attemptStatus === 'expired' && (
                  <span className="badge badge-danger">Time up — not submitted</span>
                )}
                {exam.attemptStatus === 'closed' && (
                  <span className="badge badge-ended">Closed</span>
                )}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const Exams = () => {
  const { currentUser } = useApp();
  return currentUser.role === 'student' ? <StudentExams /> : <StaffExams />;
};

export default Exams;
